"use client";

import { useRouter } from "@/lib/router/navigation";
import { Check, ChevronsUpDown, Boxes } from "lucide-react";
import { useTenancy } from "@/lib/tenancy/tenancy-context";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuLabel,
  DropdownMenuSeparator, DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { cn } from "@/lib/utils";

export function ProjectSwitcher() {
  const router = useRouter();
  const { projects, project, projectId, loading } = useTenancy();
  // "Select project" is the EMPTY-picker label, and on a project route it was
  // showing while the session was still loading — the URL already names the
  // project, so implying the person must pick one is wrong (S-250). Three
  // states: the project once resolved, a neutral "Loading…" while the session
  // is in flight on a known route, and the picker label only when neither.
  const resolving = loading || (Boolean(projectId) && !project);
  const label = project?.name ?? (resolving ? "Loading…" : "Select project");
  const initials = (project?.name ?? "··").slice(0, 2).toUpperCase();

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <button className="flex w-full items-center gap-2 rounded-md border bg-card px-2 py-1.5 text-left transition-colors hover:bg-accent">
          <span className="flex size-6 shrink-0 items-center justify-center rounded-[6px] text-[11px] font-bold text-white" style={{ backgroundColor: project?.color ?? "var(--primary)" }}>{initials}</span>
          <span className="min-w-0 flex-1">
            <span className="block truncate text-[13px] font-semibold leading-tight">{label}</span>
            <span className="block truncate text-[10px] uppercase tracking-wide text-muted-foreground">Project</span>
          </span>
          <ChevronsUpDown className="size-3.5 shrink-0 text-muted-foreground" />
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent className="w-[--radix-dropdown-menu-trigger-width] min-w-60" align="start">
        <DropdownMenuLabel className="text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">Switch project</DropdownMenuLabel>
        {projects.map((w) => (
          <DropdownMenuItem key={w.id} onClick={() => router.push(`/projects/${w.id}/dashboard`)} className="gap-2 py-2">
            <span className="flex size-6 items-center justify-center rounded-[6px] text-[11px] font-bold text-white" style={{ backgroundColor: w.color ?? "var(--primary)" }}>{w.name.slice(0, 2).toUpperCase()}</span>
            <span className="flex min-w-0 flex-1 flex-col">
              <span className="truncate text-[13px] font-medium leading-tight">{w.name}</span>
              {w.description && <span className="truncate text-xs text-muted-foreground">{w.description}</span>}
            </span>
            <Check className={cn("size-4 text-primary", project?.id === w.id ? "opacity-100" : "opacity-0")} />
          </DropdownMenuItem>
        ))}
        <DropdownMenuSeparator />
        <DropdownMenuItem onClick={() => router.push("/projects")} className="gap-2 text-muted-foreground">
          <Boxes className="size-4" /> All projects
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
